<template>
  <div>
    <v-sheet class="ma-6 pa-6" min-height="400px" outlined>

      <v-card-text >
        <div>
          <v-icon color="blue" small>mdi-heart</v-icon>
          Help
        </div>
        <div>
          Select the corresponding parameters and click
          <v-btn x-small style="text-transform: none">Submit</v-btn>, the enrichment analysis will be run on
          <a href='https://2024.webgestalt.org/' style="color: #0d47a1;font-weight: bold">WebGestalt</a> server.
        </div>
        <div>
          GSEA analysis should use all the sites in the ranked abundance list to determine if gene sets are enriched at the top or bottom, rather than just focusing on highly differentially expressed sites.
        </div>

      </v-card-text>

      <v-sheet outlined class="my-3">
        <v-card-title class="font-weight-bold" style="font-size: 1.3rem">Basic Parameters</v-card-title>
        <v-card-text>
          <v-row>
            <v-col cols="12">
              <v-row>
                <v-col cols="3">
                  <span class="font-weight-medium">Methods:</span>
                  <helpicon tooltip-text="Select a method from the dropdown menu for enrichment analysis. 1) Over-Representation Analysis uses the Fisher's exact test to analyze a user-uploaded gene list against gene sets within user selected functional database(s). 2) Gene Set Enrichment Analysis uses a modified Kolmogorov-Smirnov test to analyze a user-uploaded gene list along with appropriate rank metric values against gene sets withing user selected functional database(s)."></helpicon>
                </v-col>
                <v-col cols="9">
                  <v-radio-group v-model="enrich_method" row @change="onMethodChange">
                    <v-radio
                        label="Overrepresentation Enrichment Analysis (ORA)"
                        value="ora"
                    />
                    <v-radio
                        label="Gene Set Enrichment Analysis (GSEA)"
                        value="gsea"
                        :disabled="!isThreeColumn"
                    />
                  </v-radio-group>

                  <v-snackbar v-model="snackbar" timeout="4000" color="warning" absolute>
                    {{ snackbarMsg }}
                    <v-btn color="red" text @click="snackbar = false">Close</v-btn>
                  </v-snackbar>

                </v-col>
              </v-row>
              <v-row>
                <v-col cols="3">
                  <span class="font-weight-medium">Analyte Type:</span>
                </v-col>
                <v-col cols="9">PTM</v-col>
              </v-row>
              <v-row>
                <v-col cols="3">
                  <span class="font-weight-medium">Organism:</span>
                </v-col>
                <v-col cols="9">Homo sapiens</v-col>
              </v-row>
              <v-row>
                <v-col cols="3">
                  <span class="font-weight-medium">Functional database:</span>
                </v-col>
                <v-col cols="9">
                  <v-select
                      v-model="selectedDatabase"
                      :items="database_select"
                      dense item-text="text" item-value="value"
                      label="Database"
                      outlined style="width: 60%"
                      @change="updateDatabaseParameters"
                  ></v-select>
                </v-col>
              </v-row>
              <v-row v-show="enrich_method=='ora'" >
                <v-col cols="3">
                  <span class="font-weight-medium">Upload user referance set file (Optional):</span>
                  <helpicon tooltip-text="Upload the reference analyte list in a txt file with one column."></helpicon>
                </v-col>
                <v-col cols="9">
                  <v-file-input
                      label="Choose referance file"
                      outlined
                      dense
                      style="width: 60%"
                      v-model="parameters1.ref_file"
                      prepend-icon=""
                      accept=".txt"
                      prepend-inner-icon="mdi-file-upload"
                  ></v-file-input>
                  <v-card >
                    💡 If no file is uploaded, all sites annotated in the current database will be used as the background set. </br>
                    It is <a style="color: red">strongly recommended </a> you provide your own reference set for ORA. This reference set should be all the PTM sites that were observed.
                  </v-card>
                </v-col>
              </v-row>
            </v-col>
          </v-row>
        </v-card-text>
      </v-sheet>
      <v-expansion-panels>
        <v-expansion-panel>
          <v-expansion-panel-header class="font-weight-bold">Advanced Parameters</v-expansion-panel-header>
          <v-expansion-panel-content>
            <v-row>
              <v-col cols="3">
                <span class="font-weight-medium">Redundancy Removal: </span>
                <helpicon tooltip-text="Enable different methods for redundancy removal."></helpicon>
              </v-col>
              <v-col cols="9">
                <v-checkbox label="Weighted set cover (fast)" value="WSC" v-model="redundancy" hide-details dense />
                <v-checkbox label="Affinity Propagation" value="AP" v-model="redundancy" hide-details dense />
                <v-checkbox label="k-Medoid" value="KMED" v-model="redundancy" hide-details dense />
              </v-col>
            </v-row>
            <v-row>
              <v-col cols="3">
                <span class="font-weight-medium">minimum number of analytes for a category:</span>
                <helpicon tooltip-text="Categories with fewer analytes will be removed."></helpicon>
              </v-col>
              <v-col cols="2">
                <v-text-field
                    v-model.number="min_num"
                    dense
                    outlined
                    type="number"
                    min="1"
                />
              </v-col>
            </v-row>
            <v-row>
              <v-col cols="3">
                <span class="font-weight-medium">Maximum number of analytes for a category:</span>
                <helpicon tooltip-text="Categories with more analytes will be removed."></helpicon>
              </v-col>
              <v-col cols="2">
                <v-text-field
                    v-model.number="max_num"
                    dense
                    outlined
                    type="number"
                    min="1"
                />
              </v-col>
            </v-row>
            <v-row>
              <v-col cols="3">
                <span class="font-weight-medium">Multiple Test Adjustment:</span>
                <helpicon tooltip-text="Uses R function p.adjust."></helpicon>
              </v-col>
              <v-col cols="2">
                <v-select
                    outlined
                    v-model="fdr_method"
                    :items="fdrMethods"
                    dense
                />
              </v-col>
            </v-row>
            <v-row>
              <v-col cols="3" class="d-flex align-center">
                <span class="font-weight-medium">Significance Level:</span>
                <helpicon tooltip-text="Choose method and threshold for enriched category detection." />
              </v-col>
              <v-col cols="9">
                <v-row class="d-flex align-center">
                  <v-col cols="auto">
                    <v-radio-group
                        v-model="sig_method"
                        row
                        class="d-flex align-center"
                        style="margin-bottom: 0"
                        @change="onSigMethodChange"
                    >
                      <v-radio label="FDR" value="fdr" />
                      <v-radio label="TOP" value="top" />
                    </v-radio-group>

                  </v-col>
                  <v-col cols="auto">
                      <v-text-field
                          v-if="sig_method === 'top'"
                          outlined
                          v-model.number="sig_value"
                          dense
                          type="number"
                          min="1"
                          style="min-width: 120px"
                      />

                    <input
                        v-if="sig_method === 'fdr'"
                        v-model.number="sig_value"
                        type="text"
                        placeholder="0 ~ 1"
                        style="min-width: 120px; padding: 6px; border: 1px solid #ccc; border-radius: 4px;"
                    />


                    <!--                    <v-text-field-->
<!--                        outlined-->
<!--                        v-model.number="sig_value"-->
<!--                        dense-->
<!--                        type="number"-->
<!--                        style="max-width: 120px"-->
<!--                    />-->
                  </v-col>
                </v-row>
              </v-col>
            </v-row>
            <v-row>
              <v-col cols="3">
                <span class="font-weight-medium">Number of categories expected from set cover:</span>
                <helpicon tooltip-text="Controls set cover algorithm stopping."></helpicon>
              </v-col>
              <v-col cols="2">
                <v-text-field
                    v-model.number="set_cover_num"
                    dense
                    outlined
                    type="number"
                    min="1"
                />
              </v-col>
            </v-row>
            <v-row>
              <v-col cols="3">
                <span class="font-weight-medium">Number of clusters (k) for k-Medoid:</span>
                <helpicon tooltip-text="This is the number of clusters k-Medoid will identify."></helpicon>
              </v-col>
              <v-col cols="2">
                <v-text-field
                    v-model.number="kMedoid_k"
                    dense
                    outlined
                    type="number"
                    min="1"
                    max="100"
                />
              </v-col>
            </v-row>
            <v-row>
              <v-col cols="3">
                <span class="font-weight-medium">Number of categories visualized in the report:</span>
                <helpicon tooltip-text="Maximum categories shown in report."></helpicon>
              </v-col>
              <v-col cols="2">
                <v-text-field
                    v-model.number="report_num"
                    dense
                    outlined
                    type="number"
                    min="1"
                    max="100"
                />
              </v-col>
            </v-row>
            <v-row>
              <v-col cols="3">
                <span class="font-weight-medium">Color in DAG:</span>
                <helpicon tooltip-text="Use binary or continuous color scheme."></helpicon>
              </v-col>
              <v-col cols="2">
                <v-radio-group v-model="color_scheme" row>
                  <v-radio label="Continuous" value="continuous" />
                  <v-radio label="Binary" value="binary" />
                </v-radio-group>
              </v-col>
            </v-row>


          </v-expansion-panel-content>
        </v-expansion-panel>
      </v-expansion-panels>
      <v-sheet class="my-6">
        <v-row >
          <v-spacer></v-spacer>
          <v-col cols="2"><v-btn color="primary" style="text-transform: none" @click="submitForm" :disabled="formattedLength < 10">Submit</v-btn></v-col>



          <v-spacer></v-spacer>

        </v-row>
      </v-sheet>

    </v-sheet>

  </div>
</template>
<script>
import Helpicon from "@/components/sub/helpicon.vue";
import {apiprefix} from "@/main";

const MIN_INPUT_SIZE = 10;
const GSEA_RECOMMENDED_SIZE = 3000;
function isEnspId(x) {
  return x.startsWith('ENSP');
}

function isUniprotId(x) {
  return /^[A-Z0-9]{6,10}(-\d+)?$/.test(x);
}

function isGeneSymbolId(x) {
  return /^[A-Z][A-Z0-9-]{1,19}$/.test(x);
}

function sampleArray(arr, n) {
  if (arr.length <= n) return arr;
  const shuffled = [...arr].sort(() => 0.5 - Math.random());
  return shuffled.slice(0, n);
}

function detectIdType(substrates, options = {}) {
  const SAMPLE_SIZE = options.sampleSize || 10;
  const THRESHOLD = options.threshold || 0.7;

  if (!substrates || substrates.length === 0) {
    return null;
  }

  const sampled = sampleArray(substrates, SAMPLE_SIZE);

  let ensp = 0, uniprot = 0, gene = 0;

  sampled.forEach(x => {
    if (isEnspId(x)) ensp++;
    else if (isUniprotId(x)) uniprot++;
    else if (isGeneSymbolId(x)) gene++;
  });

  const total = sampled.length;

  if (ensp / total >= THRESHOLD) return 'phosphositeEnsembl';
  if (uniprot / total >= THRESHOLD) return 'phosphositeUniprot';
  if (gene / total >= THRESHOLD) return 'phosphositeGeneSymbol';

  return null;
}

export default {
  name: "EnrichmentForBatchSearch",
  props: ["batch_search"],
  components: { Helpicon },

  data() {
    return {
      snackbar: false,
      snackbarMsg: "",
      database_select: [
        { text: 'PTMsigDB', value: 'PTMsigDB_phosphosite' },
        { text: 'Kinase Phosphosite', value: 'Kinase_phosphosite' },
        { text: 'Hallmark Process', value: 'cancerhallmark_phosphosite' },
        { text: 'Comentioned Chemical', value: 'comention_chemical_phosphosite' },
        { text: 'Comentioned Disease', value: 'comention_disease_phosphosite' },
        { text: 'Comentioned Gene', value: 'comention_gene_phosphosite' },
        { text: 'PTMax Figure', value: 'figure_phosphosite' }
      ],
      parameters1: {
        organism: 'hsapiens',
        enriched_database_category: 'network',
        enriched_database_name: "kinase_phosphosite",
        analyte_type: 'phosphosite',
        id_type: 'phosphositeEnsembl',
        gene_list: "",
        ref_file: null
      },
      selectedDatabase: 'Kinase_phosphosite',
      enrich_method: 'ora',
      redundancy: ['WSC'],
      min_num: 3,
      max_num: 2000,
      fdrMethods: ['BH', 'BY', 'Bonferroni', 'Holm', 'Hommel'],
      fdr_method: 'BH',
      sig_method: 'top',
      sig_value: 10,
      set_cover_num: 10,
      kMedoid_k: 10,
      report_num: 40,
      color_scheme: 'continuous'
    };
  },

  computed: {
    structuredInput() {
      if (!this.batch_search) return [];
      const lines = this.batch_search.split(/[\n,]+/).map(l => l.trim()).filter(Boolean);
      const unique = [...new Set(lines)];

      return unique
          .map(line => {
            const tokens = line.split(/[\s:]+/).map(t => t.trim()).filter(Boolean);
            const substrate = tokens[0]?.toUpperCase() || '';
            const site = tokens[1]?.toUpperCase() || '';
            const third = tokens[2];
            const value = Number(third);
            const valid =
                substrate &&
                site &&
                (tokens.length < 3 || Number.isFinite(value));

            return valid
                ? { substrate, site, user_input_score: Number.isFinite(value) ? value : null }
                : null;
          })
          .filter(Boolean);
    },

    isThreeColumn() {
      return this.structuredInput.some(x => x.user_input_score !== null);
    },

    formattedLength() {
      return this.structuredInput.length;
    }
  },

  watch: {
    batch_search() {
      this.enrich_method = this.isThreeColumn ? 'gsea' : 'ora';
    }
  },

  methods: {
    onSigMethodChange(val) {
      this.sig_value = val === 'top' ? 10 : 0.05;
    },

    updateDatabaseParameters() {
      const db = this.selectedDatabase;
      this.parameters1.enriched_database_category =
          db.includes('PTMsigDB_') || db.includes('Kinase_') ? 'network' : 'PTMax';
      this.parameters1.enriched_database_name = db;
    },

    onMethodChange(val) {
      if (val === 'gsea' && this.structuredInput.length < GSEA_RECOMMENDED_SIZE) {
        this.snackbarMsg =
            "GSEA analysis should use all the sites in the ranked abundance list to determine if gene sets are enriched at the top or bottom, rather than just focusing on highly differentially expressed sites.";
        this.snackbar = true;
      }
    },



    submitForm() {
      const structured = this.structuredInput;

      if (structured.length < MIN_INPUT_SIZE) {
        this.snackbarMsg = `At least ${MIN_INPUT_SIZE} sites are required, but you only provided ${structured.length}.`;
        this.snackbar = true;
        return;
      }

      const substrates = structured
          .map(x => (x.substrate || '').trim().toUpperCase())
          .filter(x => x.length > 0);

      const idType = detectIdType(substrates, {
        sampleSize: 10,
        threshold: 0.7
      });

      // console.log({
      //   detectedIdType: idType
      // });

      if (!idType) {
        this.snackbarMsg =
            "Mixed or invalid identifier formats detected. Please use a consistent ID type.";
        this.snackbar = true;
        return;
      }
//
// // 1) ENSP: Ensembl protein
//       const isEnsp = substrates.length > 0 && substrates.every(x => x.startsWith('ENSP'));
//
// // 2) UniProt accession (canonical + isoform)
// // - canonical examples: O15440, Q8IZP0, P68133
// // - isoform examples: P42684-10, Q9H4G0-4
//       const isUniprot = substrates.length > 0 && substrates.every(x => /^[A-Z0-9]{6,10}(-\d+)?$/.test(x));
//
// // 3) Gene Symbol (allow dash like H1-4)
//       const isGeneSymbol = substrates.length > 0 && substrates.every(x => /^[A-Z][A-Z0-9-]{1,19}$/.test(x));
//
// // Decide idType with clear precedence
//       let idType = null;
//       if (isEnsp) {
//         idType = 'phosphositeEnsembl';
//       } else if (isUniprot) {
//         idType = 'phosphositeUniprot';
//       } else if (isGeneSymbol) {
//         idType = 'phosphositeGeneSymbol';
//       } else {
//         this.snackbarMsg =
//             "Mixed or invalid identifier formats detected. Please use consistent identifiers (ENSP..., UniProt accessions, or Gene Symbols) across all lines.";
//         this.snackbar = true;
//         return;
//       }


      const useGSEA = this.isThreeColumn;
      const enrichMethod = useGSEA ? 'GSEA' : 'ORA';

      const geneListText = structured
          .map(x => useGSEA ? `${x.substrate}_${x.site}\t${x.user_input_score}` : `${x.substrate}_${x.site}`)
          .join('\n');

      const formData = {
        enrich_method: enrichMethod,
        organism: "hsapiens",
        "enriched_database_category[]": this.parameters1.enriched_database_category,
        "enriched_database_name[]": this.selectedDatabase,
        gene_list: geneListText,
        id_type: idType,
        ref_set: "phosphosite",
        ref_id_type: idType,
        min_num: String(this.min_num),
        max_num: String(this.max_num),
        fdr_method: this.fdr_method,
        sig_method: this.sig_method,
        sig_value: String(this.sig_value),
        set_cover_num: String(this.set_cover_num),
        kMedoid_k: String(this.kMedoid_k),
        report_num: String(this.report_num),
        color_scheme: this.color_scheme
      };

      this.redundancy.forEach(m => {
        if (m === 'WSC') formData.hasWSC = 'on';
        if (m === 'AP') formData.hasAP = 'on';
        if (m === 'KMED') formData.hasKMED = 'on';
      });

      const form = document.createElement("form");
      form.method = "POST";
      form.action = "https://www.webgestalt.org/process.php";
      form.target = "_blank";
      form.style.display = "none";

      Object.entries(formData).forEach(([k, v]) => {
        const input = document.createElement("input");
        input.type = "hidden";
        input.name = k;
        input.value = v;
        form.appendChild(input);
      });

      if (this.parameters1.ref_file instanceof File) {
        const fileInput = document.createElement("input");
        fileInput.type = "file";
        fileInput.name = "ref_file";
        form.appendChild(fileInput);
      }

      document.body.appendChild(form);
      form.submit();
      setTimeout(() => document.body.removeChild(form), 1000);
    }
  }
};
</script>


<!--<script>-->
<!--import Helpicon from "@/components/sub/helpicon.vue";-->

<!--export default {-->
<!--  name: "EnrichmentForBatchSearch",-->
<!--  props: ["batch_search"],-->
<!--  components: {Helpicon},-->
<!--  data() {-->
<!--    return {-->
<!--      snackbar: false,-->
<!--      snackbarMsg: "",-->
<!--      autoMethodBound: false,-->
<!--      database_select: [-->
<!--        {text: 'PTMsigDB', value: 'PTMsigDB_phosphosite'},-->
<!--        {text: 'Kinase Phosphosite', value: 'Kinase_phosphosite'},-->
<!--        {text: 'Cancer Hallmark', value: 'cancerhallmark_phosphosite'},-->
<!--        // {text: 'Comentioned Cellline', value: 'comention_cellline_phosphosite'},-->
<!--        {text: 'Comentioned Chemical', value: 'comention_chemical_phosphosite'},-->
<!--        {text: 'Comentioned Disease', value: 'comention_disease_phosphosite'},-->
<!--        {text: 'Comentioned Gene', value: 'comention_gene_phosphosite'},-->
<!--        {text: 'PTMax Figure', value: 'figure_phosphosite'}-->
<!--      ],-->
<!--      parameters1: {-->
<!--        organism: 'hsapiens',-->
<!--        enriched_database_category: 'network',-->
<!--        enriched_database_name: "kinase_phosphosite",-->
<!--        analyte_type: 'phosphosite',-->
<!--        id_type: 'phosphositeEnsembl',-->
<!--        gene_list: "",-->
<!--        ref_file: null,-->
<!--      },-->
<!--      selectedDatabase: 'Kinase_phosphosite',-->
<!--      enrich_method:'ora',-->
<!--      redundancy: ['WSC'],-->
<!--      min_num: 3,-->
<!--      max_num: 2000,-->
<!--      fdrMethods: ['BH', 'BY', 'Bonferroni', 'Holm', 'Hommel'],-->
<!--      fdr_method: 'BH',-->
<!--      sig_method: 'top',-->
<!--      sig_value: 10,-->
<!--      set_cover_num: 10,-->
<!--      kMedoid_k: 10,-->
<!--      report_num: 40,-->
<!--      color_scheme: 'continuous'-->
<!--    }-->
<!--  },-->
<!--  methods: {-->
<!--    onSigMethodChange(val) {-->
<!--      if (val === "top") {-->
<!--        this.sig_value = 10-->
<!--      } else if (val === "fdr") {-->
<!--        this.sig_value = 0.05-->
<!--      }-->
<!--    },-->
<!--    formatToDict(inputText) {-->
<!--      const lines = inputText.split(/[\n,]+/).map(l => l.trim()).filter(Boolean);-->
<!--      const uniqueLines = [...new Set(lines)];-->
<!--      return uniqueLines-->
<!--          .map(line => {-->
<!--            const tokens = line.split(/[\s:]+/).map(t => t.trim()).filter(Boolean);-->
<!--            const substrate = tokens[0]?.toUpperCase() || '';-->
<!--            const site = tokens[1]?.toUpperCase() || '';-->
<!--            const third = tokens[2];-->
<!--            const hasThirdColumn = tokens.length >= 3;-->
<!--            const thirdIsValidNumber = third !== undefined && !isNaN(Number(third));-->
<!--            const valid = substrate && site && (!hasThirdColumn || thirdIsValidNumber);-->
<!--            return valid ? { substrate, site, user_input_score: thirdIsValidNumber ? third : null } : null;-->
<!--          })-->
<!--          .filter(item => item !== null);-->
<!--    },-->
<!--    updateDatabaseParameters() {-->
<!--      const db = this.selectedDatabase;-->

<!--      if (db.includes('PTMsigDB_') || db.includes('Kinase_')) {-->
<!--        this.parameters1.enriched_database_category = 'network';-->
<!--      } else {-->
<!--        this.parameters1.enriched_database_category = 'PTMax';-->
<!--      }-->


<!--      this.parameters1.enriched_database_name = db;-->
<!--    },-->
<!--    onMethodChange(val) {-->
<!--      if (val === "gsea") {-->
<!--        const structured = this.formatToDict(this.batch_search);-->
<!--        const threshold = 3000;-->

<!--        if (structured.length < threshold) {-->
<!--          this.snackbarMsg =-->
<!--              "GSEA analysis should use all the sites in the ranked abundance list to determine if gene sets are enriched at the top or bottom, rather than just focusing on highly differentially expressed sites.";-->
<!--          this.snackbar = true;-->
<!--        }-->
<!--      }-->
<!--    },-->

<!--    submitForm() {-->
<!--      const structured = this.formatToDict(this.batch_search);-->
<!--      // console.log(structured)-->
<!--      if (structured.length < 10) {-->
<!--        this.snackbarMsg = `At least 10 sites are required, but you only provided ${structured.length}.`;-->
<!--        this.snackbar = true;-->
<!--        return;-->
<!--      }-->

<!--      if (!structured.length) {-->
<!--        this.snackbarMsg = "No valid input.";-->
<!--        this.snackbar = true;-->
<!--        return;-->
<!--      }-->

<!--      const isThreeColumn = structured.some(item => item.user_input_score !== null);-->
<!--      const useGSEA = isThreeColumn;-->
<!--      const enrichMethod = useGSEA ? "GSEA" : "ORA";-->

<!--      if (!useGSEA && this.enrich_method === "gsea") this.enrich_method = "ora";-->
<!--      if (useGSEA && this.enrich_method === "ora") this.enrich_method = "gsea";-->

<!--      const processedList = structured.map(item =>-->
<!--          isThreeColumn-->
<!--              ? `${item.substrate}_${item.site}\t${item.user_input_score}`-->
<!--              : `${item.substrate}_${item.site}`-->
<!--      );-->

<!--      const substrates = structured.map(item => item.substrate);-->
<!--      let idType = 'phosphositeGeneSymbol';-->

<!--      if (substrates.every(x => x.toLowerCase().startsWith('ensp'))) {-->
<!--        idType = 'phosphositeEnsembl';-->
<!--      } else if (substrates.every(x => /^[a-zA-Z][0-9]/.test(x))) {-->
<!--        idType = 'phosphositeUniprot';-->
<!--      }-->
<!--      const geneListText = processedList.map(x => x.trim()).join('\n').trim();-->
<!--      const formData = {-->
<!--        enrich_method: enrichMethod,-->
<!--        organism: "hsapiens",-->
<!--        "enriched_database_category[]": this.parameters1.enriched_database_category,-->
<!--        "enriched_database_name[]": this.selectedDatabase,-->
<!--        gene_list: geneListText,-->
<!--        id_type: idType,-->
<!--        ref_set: "phosphosite",-->
<!--        ref_file: this.parameters1.ref_file,-->
<!--        ref_id_type:idType,-->
<!--        min_num: this.min_num.toString(),-->
<!--        max_num: this.max_num.toString(),-->
<!--        fdr_method: this.fdr_method,-->
<!--        sig_method: this.sig_method,-->
<!--        sig_value: this.sig_value.toString(),-->
<!--        set_cover_num: this.set_cover_num.toString(),-->
<!--        kMedoid_k: this.kMedoid_k.toString(),-->
<!--        report_num: this.report_num.toString(),-->
<!--        color_scheme: this.color_scheme-->
<!--      };-->

<!--      this.redundancy.forEach(method => {-->
<!--        if (method === "WSC") formData.hasWSC = "on";-->
<!--        if (method === "AP") formData.hasAP = "on";-->
<!--        if (method === "KMED") formData.hasKMED = "on";-->
<!--      });-->

<!--      const form = document.createElement("form");-->
<!--      form.method = "POST";-->
<!--      form.action = "https://www.webgestalt.org/process.php";-->
<!--      form.target = "_blank";-->
<!--      form.style.display = "none";-->

<!--      // console.log(formData)-->

<!--      for (const key in formData) {-->
<!--        if (formData.hasOwnProperty(key)) {-->
<!--          const input = document.createElement("input");-->
<!--          input.type = "hidden";-->
<!--          input.name = key;-->
<!--          input.value = formData[key];-->
<!--          form.appendChild(input);-->
<!--        }-->
<!--      }-->
<!--      document.body.appendChild(form);-->
<!--      form.submit();-->

<!--      setTimeout(() => {-->
<!--        document.body.removeChild(form);-->
<!--      }, 1000);-->
<!--    },-->
<!--  },-->
<!--  mounted() {-->
<!--  },-->
<!--  computed: {-->
<!--    isThreeColumn() {-->
<!--      if (!this.batch_search) return false;-->
<!--      const lines = this.batch_search.split(/\r?\n/).map(l => l.trim()).filter(Boolean);-->
<!--      return lines.some(line => {-->
<!--        const tokens = line.split(/[\s\t:]+/).filter(Boolean);-->
<!--        return tokens.length >= 3;-->
<!--      });-->
<!--    },-->
<!--    formattedLength() {-->
<!--      if (!this.batch_search) return 0;-->
<!--      return this.formatToDict(this.batch_search).length;-->
<!--    }-->
<!--  },-->
<!--  watch: {-->
<!--    batch_search(val) {-->
<!--      if (!val) return;-->
<!--      const structured = this.formatToDict(val);-->
<!--      const hasValue = structured.some(x => x.user_input_score !== null);-->
<!--      this.enrich_method = hasValue ? 'gsea' : 'ora';-->
<!--    }-->
<!--  }-->


<!--}-->
<!--</script>-->

<style type="text/scss" lang="scss" scoped>

</style>